/**
 * Msg 604 · Phase B — Module-level singleton cache for
 * `atlas_attributes` (the Global Attributes Library). Mirrors the
 * Phase A categories cache pattern: one fetch per session, shared
 * across every folder edit form / picker / preset row / search
 * indexer. Cache invalidates + re-fetches on any admin CRUD so
 * a rename in the library cascades to every mounted subscriber
 * immediately.
 */
import React from "react";
import { api } from "@/lib/api";


let _attributes = [];
let _byId = new Map();
let _byKey = new Map();
let _loaded = false;
let _fetching = null;
const _subscribers = new Set();


function _notify() {
  _subscribers.forEach((fn) => {
    try { fn(_attributes); } catch { /* noop */ }
  });
}


export async function loadAttributes(force = false) {
  if (_loaded && !force && !_fetching) return _attributes;
  if (_fetching) return _fetching;
  _fetching = (async () => {
    try {
      const r = await api.get("/atlas/attributes");
      _attributes = r.data?.attributes || [];
      _byId = new Map(_attributes.map((a) => [a.id, a]));
      _byKey = new Map(_attributes.map((a) => [a.key, a]));
      _loaded = true;
      _notify();
    } catch {
      _attributes = [];
      _byId = new Map();
      _byKey = new Map();
      _loaded = true;
    } finally {
      _fetching = null;
    }
    return _attributes;
  })();
  return _fetching;
}


export function getAttributesSync() {
  if (!_loaded) loadAttributes();
  return _attributes;
}


export function getAttributeById(id) {
  if (!id) return null;
  return _byId.get(id) || null;
}


export function getAttributeByKey(key) {
  if (!key) return null;
  return _byKey.get(key) || null;
}


export function subscribeAttributes(fn) {
  _subscribers.add(fn);
  return () => _subscribers.delete(fn);
}


export function invalidateAttributes() {
  _loaded = false;
  return loadAttributes(true);
}


/**
 * 2026-08-06 — Optimistic removal. `invalidateAttributes()` is
 * fire-and-forget, so the network round-trip left the deleted row
 * ghost-visible on screen until the refetch resolved. Callers now
 * splice it out of the cache synchronously and notify subscribers
 * before firing the full refetch behind it.
 */
export function removeAttributeById(id) {
  if (!id) return;
  const next = _attributes.filter((a) => a.id !== id);
  if (next.length === _attributes.length) return;
  _attributes = next;
  _byId.delete(id);
  for (const [k, v] of _byKey.entries()) {
    if (v && v.id === id) _byKey.delete(k);
  }
  _notify();
}


/**
 * React hook. Returns the current library array + auto-re-renders
 * on cache invalidation. Never suspends — first render sees [] until
 * the initial fetch resolves.
 */
export function useAtlasAttributes() {
  const [attrs, setAttrs] = React.useState(_attributes);
  React.useEffect(() => {
    if (!_loaded) loadAttributes();
    const unsub = subscribeAttributes(setAttrs);
    return unsub;
  }, []);
  return attrs;
}
